<template>
    <div>
        <!-- Main Container -->
        <!-- Shows content only when a device is selected -->
        <v-row v-if="currentConfiguration">
            <v-col :lg="currentSelectedConfiguration ? 3 : 12" :md="currentSelectedConfiguration ? 4 : 12"
                :xl="currentSelectedConfiguration ? 2 : 12" class="border-right-divider" cols="12">

                <!-- Left Sidebar: Phone List Section -->
                <!-- Displays list of phones with search functionality -->

                <!-- Search Box -->
                <!-- Only visible when device list is shown and devices exist -->
                <!-- Allows filtering the phone list by name -->
                <v-text-field
                    v-if="hideMobileListingForMobileIfListingItemSelectedForConfigurations() && hasConfigurations"
                    v-model="configurationFilterValue" :label="$gettext('Search phones')" append-icon="mdi-magnify"
                    class="search-listing-autocomplete" clearable></v-text-field>

                <!-- Phone List -->
                <!-- Shows scrollable list of phones when devices exist -->
                <!-- Each item shows device name and MDM ID -->
                <v-list
                    v-if="currentViewConfigurationCount() > 0 && hideMobileListingForMobileIfListingItemSelectedForConfigurations()"
                    class="v-list-wrapper">

                    <v-list-item-group v-model="currentSelectedConfiguration">

                        <v-list-item v-for="configuration in configurations"
                            v-show="configurationSelectedForMobile() && checkEachConfiguration(configuration.key)"
                            :key="configuration.key"
                            :class="{ 'v-list-item v-item--active v-list-item--active primary--text': currentSelectedConfiguration === configuration.key }"
                            :value="configuration.key" class="px-0 cursor-pointer" ripple
                            @click="selectConfiguration(configuration.key)">
                            <v-list-item-content>
                                <v-list-item-title class="text-left">
                                    <v-row>
                                        <v-col class="body-2" cols="12">
                                            <span class="pl-2" v-text="configuration.key"></span>
                                        </v-col>
                                    </v-row>
                                </v-list-item-title>
                                <v-list-item-subtitle v-if="configuration.value.userInfo.mdmDeviceId"
                                    class="text-left pl-2 text-truncate caption grey--text font-weight-light">
                                    {{ configuration.value.userInfo.mdmDeviceId }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list-item-group>
                </v-list>

                <!-- No Results Message -->
                <v-row v-else-if="configurationFilterValue.length > 0" class="mx-1 mt-0 mb-3">
                    <v-col>
                        {{
                            $gettextInterpolate($gettext('No matching phones found for your search: %{
                        configurationFilterValue }'), {configurationFilterValue: configurationFilterValue})
                            }}
                    </v-col>
                </v-row>

                <!-- No Phones Message -->
                <v-row v-else class="mx-1 mt-0 mb-3">
                    <v-col>
                        {{ $gettext('No phones configured yet. Please add a phone device from the users tab.') }}
                    </v-col>
                </v-row>
            </v-col>

            <!-- Right Column: Global Usage Time Settings -->

            <!-- Right Column: Main Content Area
                 Contains time restriction rules and their associated apps
                 Shows:
                 - List of time restriction rules with their settings
                 - Apps assigned to each rule in a compact view
                 - Edit/delete controls for rules
                 - Add new time condition button
                 - Expanded edit view when editing a rule
                 
                 Layout:
                 - Responsive column sizing based on if configuration selected
                 - Each rule shows:
                   - Rule name and type icon
                   - Time settings (range, personal control, etc)
                   - Associated apps in draggable container
                   - Edit/delete controls
                 - Edit mode expands to show full rule settings
                 - App store integration for adding apps
            -->
            <v-col :lg="currentSelectedConfiguration ? 9 : 12" :md="currentSelectedConfiguration ? 8 : 12"
                :xl="currentSelectedConfiguration ? 10 : 12" class="border-right-divider" cols="12">

                <!-- Concise view for each restriction (see RestrictionConciseView.vue for details) -->
                <template v-if="currentConfiguration && !editingRestrictionId">
                    <restriction-concise-view v-for="restriction in sortedAppUsageRestrictionForCurrentConfiguration"
                        :key="restriction.id" :restriction="restriction" :icons="icons" :week-days-array="weekDaysArray"
                        :is-day-selected="isDaySelected" :seconds-to-hhmm="secondsToHHMM" :$gettext="$gettext"
                        :is-editing-time-restrictions="isEditingTimeRestrictions"
                        :editing-restriction-id="editingRestrictionId" :show-menu="showMenu" :x="x" :y="y"
                        :is-dragging="isDragging" :current-selected-configuration="currentSelectedConfiguration"
                        :chosen-model="chosenModel" :get-phone-type-dir-for-device="getPhoneTypeDirForDevice"
                        :update-show-menu="updateShowMenu" :remove-app-from-restriction="removeAppFromRestriction"
                        :get-system-or-normal-rule-name="getSystemOrNormalRuleName"
                        :is-global-usage-rule="isGlobalUsageRule" :toggle-all-days="toggleAllDays"
                        :update-allowed-time-from-hhmm="updateAllowedTimeFromHHMM"
                        :sanitize-time-value="sanitizeTimeValue" @edit="setEditingRestrictionId"
                        @delete="deleteRestriction" @drag-started="handleDragStarted" @drag-ended="handleDragEnded" />
                </template>

                <!-- Row for displaying time condition details in a concise view
                     Shows:
                     - Personal control range (start/end times)
                     - Selected weekdays 
                     - Type indicator ('exclusive' for night break/global usage time, or regular time condition)
                     - Total allowed duration in minutes
                     Appears below the app icons when not in editing mode -->
                <v-row v-if="currentConfiguration && editingRestrictionId && localRestriction" class="pl-3 mb-0 pb-0">

                    <v-col class="text-left" cols="12">
                        <!-- Back/Cancel Button -->
                        <v-btn class="primary--text" small @click="cancelEditing">
                            {{ $gettext('Cancel') }}
                        </v-btn>
                    </v-col>
                    <v-col cols="12">

                        <v-row>

                            <v-col cols="5">

                                <div class="text-h6 font-weight-regular text-left mt-4 mb-2 d-flex">
                                    <v-icon class="mr-2">
                                        {{
                                            localRestriction.type === 'exclusive' ? icons.mdiWeatherNight :
                                                icons.mdiChartPie
                                        }}
                                    </v-icon>

                                    <!-- Rule name display/edit -->

                                    <div class="d-flex align-center">
                                        <template
                                            v-if="editingRestrictionId && !isASystemConfiguration(localRestriction.id)">
                                            <v-text-field v-model="localRestriction.ruleName"
                                                :label="$gettext('Rule Name')" class="mt-0 pt-0" dense hide-details
                                                outlined></v-text-field>
                                        </template>
                                        <template v-else>
                                            {{ localRestriction.ruleName }}
                                        </template>
                                    </div>
                                </div>

                                <div class="text-h6 mt-4 mb-4 text-left d-flex">
                                    {{ $gettext('Time Window') }}
                                </div>

                                <v-row>
                                    <v-col class="mb-0 pb-0" cols="12" sm="6">
                                        <v-text-field v-model="localRestriction.startTime" :label="$gettext('From')"
                                            dense outlined type="time"
                                            :error-messages="getStartTimeErrors(localRestriction)"
                                            @blur="touchStartTime(localRestriction)"
                                            @input="touchStartTime(localRestriction)">
                                        </v-text-field>
                                    </v-col>
                                    <v-col class="mb-0 pb-0" cols="12" sm="6">
                                        <v-text-field v-model="localRestriction.endTime" :label="$gettext('To')" dense
                                            outlined type="time" :error-messages="getEndTimeErrors(localRestriction)"
                                            @blur="touchEndTime(localRestriction)"
                                            @input="touchEndTime(localRestriction)">
                                        </v-text-field>
                                    </v-col>
                                    <v-col class="mt-0 pt-0" cols="12" sm="6">

                                        <v-text-field :label="$gettext('Overall Time (HH:MM)')"
                                            :value="secondsToHHMM(localRestriction.allowedTime)" dense outlined
                                            placeholder="HH:MM"
                                            @input="val => updateAllowedTimeFromHHMM(val, localRestriction)"></v-text-field>

                                    </v-col>
                                    <v-col v-if="localRestriction.type === 'quota'" class="mt-0 pt-0" cols="12" sm="6">
                                        <v-text-field v-model.number="localRestriction.maxByDay"
                                            :label="$gettext('Max/Day')" dense min="1" outlined type="number"
                                            @change="sanitizeTimeValue($event, 'maxByDay', localRestriction)"
                                            @input="sanitizeTimeValue($event, 'maxByDay', localRestriction)"></v-text-field>
                                    </v-col>
                                </v-row>

                                <!-- Days Selection -->
                                <div v-if="localRestriction.type === 'quota'" class="mb-4">
                                    <div class="subtitle-1 mb-2 text-left">
                                        {{ $gettext('Please pick the day(s)') }}
                                    </div>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-checkbox
                                                :input-value="localRestriction.weekDays && localRestriction.weekDays.length === 7"
                                                :label="$gettext('All Days')" class="mb-2" dense hide-details
                                                @change="toggleAllDays(localRestriction)" />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-checkbox v-model="localRestriction.weekDays" :label="$gettext('Sunday')"
                                                :value="1" class="mb-2" dense hide-details></v-checkbox>
                                            <v-checkbox v-model="localRestriction.weekDays" :label="$gettext('Monday')"
                                                :value="2" class="mb-2" dense hide-details></v-checkbox>
                                            <v-checkbox v-model="localRestriction.weekDays" :label="$gettext('Tuesday')"
                                                :value="3" class="mb-2" dense hide-details></v-checkbox>
                                            <v-checkbox v-model="localRestriction.weekDays"
                                                :label="$gettext('Wednesday')" :value="4" class="mb-2" dense
                                                hide-details></v-checkbox>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-checkbox v-model="localRestriction.weekDays"
                                                :label="$gettext('Thursday')" :value="5" class="mb-2" dense
                                                hide-details></v-checkbox>
                                            <v-checkbox v-model="localRestriction.weekDays" :label="$gettext('Friday')"
                                                :value="6" class="mb-2" dense hide-details></v-checkbox>
                                            <v-checkbox v-model="localRestriction.weekDays"
                                                :label="$gettext('Saturday')" :value="7" class="mb-2" dense
                                                hide-details></v-checkbox>
                                        </v-col>
                                    </v-row>
                                </div>

                                <!-- Quota Fields in One Line -->
                                <div>
                                    <v-row>
                                        <v-col v-if="localRestriction.type === 'quota'" cols="12" sm="4">
                                            <v-text-field v-model.number="localRestriction.maxByWeek"
                                                :label="$gettext('Max/Week')" dense min="1" outlined type="number"
                                                @change="sanitizeTimeValue($event, 'maxByWeek', localRestriction)"
                                                @input="sanitizeTimeValue($event, 'maxByWeek', localRestriction)"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-col>
                            <v-col>
                                <v-row>
                                    <!-- Left Side: Allowed Apps -->
                                    <v-col cols="12">
                                        <div class="d-flex flex-wrap">
                                            <v-row>
                                                <v-col :class="{ 'droppable-highlight': isDragging }" cols="12">
                                                    <draggable v-model="localRestriction.apps"
                                                        :group="{ name: 'apps', put: true }"
                                                        class="d-flex flex-wrap draggable-container"
                                                        @add="handleAppAddedToLocalRestriction" @end="handleDragEnded"
                                                        @start="handleDragStarted">
                                                        <div v-for="app in localRestriction.apps" :key="app.packageId"
                                                            class="text-center mr-4 mb-4 app-icon-container">
                                                            <app-icon-for-usage-time :app-name="app.appName"
                                                                :device-name="currentSelectedConfiguration"
                                                                :folder-name="getPhoneTypeDirForDevice(chosenModel)"
                                                                :package-name="app.packageId" :show-menu="showMenu"
                                                                :x="x" :y="y" class="mx-auto"
                                                                @changeShowMenu="updateShowMenu"
                                                                @removeAppFromRestriction="removeAppFromLocalRestriction(app.packageId)" />
                                                        </div>
                                                    </draggable>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </v-col>

                                    <!-- Right Side: App Store (Only when editing) -->
                                    <v-col cols="12">
                                        <app-store-for-usage-time :chosen-model="chosenModel"
                                            :device-name="currentSelectedConfiguration"
                                            :folder-name="getPhoneTypeDirForDevice(chosenModel, true) || ''"
                                            :is-dragging="isDragging"
                                            @app-installed="(app) => handleAppInstalledToLocal(app)"
                                            @app-removed-from-restriction="(app) => removeAppFromLocalRestriction(app.packageId)"
                                            @drag-started="handleDragStarted" @drag-ended="handleDragEnded" />
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-col>

                    <!-- Save and Cancel Buttons -->
                    <v-col cols="12" class="mt-4">
                        <v-row>
                            <v-col cols="12" class="d-flex justify-start">
                                <v-btn class="mr-2" @click="cancelEditing">
                                    {{ $gettext('Cancel') }}
                                </v-btn>
                                <v-btn color="primary" @click="saveRestriction">
                                    {{ $gettext('Save') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>

                <v-btn v-if="!editingRestrictionId" class="mt-6 mb-4 w-100" color="primary"
                    @click="addNewTimeCondition">
                    {{ $gettext('Add Time Condition') }}
                </v-btn>
            </v-col>
        </v-row>

        <snackbar ref="snackbar" />

        <time-condition-edit ref="timeConditionEdit" @create="handleCreate" @update="handleUpdate" />

        <v-dialog v-model="showEditDialog" max-width="600px">
            <v-card>
                <v-card-title>
                    {{ $gettext('Edit Global Usage Time Apps') }}
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="appSearchQuery" :label="$gettext('Search Apps')" clearable dense
                                outlined></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-list>
                                <v-list-item v-for="app in filteredAvailableApps" :key="app.packageId"
                                    @click="toggleApp(app)">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ app.appName }}</v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-checkbox :input-value="isAppSelected(app)"
                                            @click.stop="toggleApp(app)"></v-checkbox>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showEditDialog = false">{{ $gettext('Cancel') }}</v-btn>
                    <v-btn color="primary" @click="saveChanges">{{ $gettext('Save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState, mapWritableState } from 'pinia'
import { usePhonesStore } from '@/stores/PhonesStore'
import { useAppUsageRestrictionsStore } from '@/stores/AppUsageRestrictionsStore'
import MixinListing from '@/mixins/listing/MixinListing'
import MixinDark from '@/mixins/database/MixinDark'
import MixinIcon from '@/mixins/MixinIcon'
import Snackbar from '@/components/Snackbar.vue'
import draggable from 'vuedraggable'
import AppIcon from '@/components/devices/AppIcon.vue'
import { getPhoneTypeDirForDevice } from '@/composables/getPhoneTypeDirForDevice'
import MixinApps from '@/mixins/apps/MixinApps'
import TimeConditionEdit from '@/components/time_conditions/TimeConditionEdit.vue'
import AppStoreForUsageTime from '@/components/devices/AppStoreForUsageTime.vue'
import AppIconForUsageTime from "./devices/AppIconForUsageTime.vue";
import MixinSwalConfirmation from "../mixins/swal/MixinSwalConfirmation";
import RestrictionConciseView from "./tabs/usage_time/RestrictionConciseView.vue";
import MixinPersonalControl from "@/mixins/MixinPersonalControl";

export default {
    name: 'UsageTimeTab',

    components: {
        RestrictionConciseView,
        Snackbar,
        draggable,
        AppIconForUsageTime,
        TimeConditionEdit,
        AppStoreForUsageTime
    },

    mixins: [
        MixinListing,
        MixinDark,
        MixinApps,
        MixinIcon,
        MixinSwalConfirmation,
        MixinPersonalControl
    ],

    data() {
        return {
            weekDaysArray: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
            configurationFilterValue: '',
            allowedApps: [],
            availableApps: [],
            selectedGroups: [],
            allGroupsForAutocomplete: [],
            appFilterText: '',
            showMenu: { value: false, packageName: '' },
            x: 0,
            y: 0,
            isEditing: false,
            showEditDialog: false,
            appSearchQuery: '',
            /**
             * @editingRestrictionId
             * This holds the current restriction id.
             * When changed (tracked via watch): it sets the value false for isEditingTimeRestrictions and isEditingApps
             */
            editingRestrictionId: null,
            /**
             * @isEditingTimeRestrictions
             * This holds the value true when the time restrictions are being edited.
             */
            isEditingTimeRestrictions: false,
            /**
             * @isEditingApps
             * This holds the value true when the apps are being edited.
             */
            isEditingApps: false,
            isDragging: false,
            startTimeTouched: false,
            endTimeTouched: false,
            localRestriction: null,
        }
    },

    computed: {
        ...mapWritableState(usePhonesStore, {
            configurations: 'configurations',
            currentSelectedConfiguration: 'currentSelectedConfiguration',
            currentSelectedConfigurationIndex: 'currentSelectedConfigurationIndex',
            chosenModel: 'chosenModel'
        }),
        ...mapState(useAppUsageRestrictionsStore, {
            restrictions: 'restrictions'
        }),
        weekDays() {
            return [
                { label: this.$gettext('Sun'), value: 1 },
                { label: this.$gettext('Mon'), value: 2 },
                { label: this.$gettext('Tue'), value: 3 },
                { label: this.$gettext('Wed'), value: 4 },
                { label: this.$gettext('Thu'), value: 5 },
                { label: this.$gettext('Fri'), value: 6 },
                { label: this.$gettext('Sat'), value: 7 }
            ];
        },
        currentConfiguration() {
            if (!this.currentSelectedConfiguration || !this.configurations) {
                return null;
            }
            const config = this.configurations.find(config =>
                config.key === this.currentSelectedConfiguration
            );
            return config || null;
        },
        hasConfigurations() {
            return this.configurations.length > 0
        },
        filteredApps() {
            if (!this.appFilterText) return this.availableApps;

            return this.availableApps.filter(app =>
                app.appName.toLowerCase().includes(this.appFilterText.toLowerCase())
            );
        },
        installedApps() {
            if (!this.currentConfiguration?.value?.installed_applications) {
                return [];
            }
            return this.currentConfiguration.value.installed_applications;
        },
        filteredAvailableApps() {
            if (!this.appSearchQuery) return this.installedApps;
            const query = this.appSearchQuery.toLowerCase();
            return this.installedApps.filter(app =>
                app.appName.toLowerCase().includes(query)
            );
        },
        appUsageRestrictionForCurrentConfiguration() {
            if (!this.currentSelectedConfiguration || !this.configurations) return null;

            const currentConfig = this.configurations.find(
                config => config.key === this.currentSelectedConfiguration
            );

            if (!currentConfig?.value?.userInfo?.mdmDeviceId) return null;

            const mdmDeviceId = currentConfig.value.userInfo.mdmDeviceId;
            const restrictionData = this.restrictions[mdmDeviceId];

            return restrictionData?.rules || [];
        },
        sortedAppUsageRestrictionForCurrentConfiguration() {
            if (!this.appUsageRestrictionForCurrentConfiguration) return null;

            // Create a copy of the array to avoid mutating the original
            const restrictions = [...this.appUsageRestrictionForCurrentConfiguration];

            // Sort the array so that Global Usage Time/Night Break comes first
            return restrictions.sort((a, b) => {
                const aIsGlobal = this.isGlobalUsageRule(a);
                const bIsGlobal = this.isGlobalUsageRule(b);

                if (aIsGlobal && !bIsGlobal) return -1;
                if (!aIsGlobal && bIsGlobal) return 1;
                return 0;
            });
        },
        getStartTimeErrors() {
            return (restriction) => {
                const errors = [];

                if (!this.startTimeTouched) {
                    return errors;
                }

                if (restriction.startTime && restriction.endTime) {
                    const timeDiff = this.calculateTimeDifference(restriction.startTime, restriction.endTime);

                    if (timeDiff !== null) {
                        if (timeDiff <= 0) {
                            errors.push(this.$gettext('End time must be after start time'));
                        } /* else if (timeDiff < 60) {
                            errors.push(this.$gettext('Time window must be at least 1 hour'));
                        }*/
                    }
                }

                return errors;
            };
        },
        getEndTimeErrors() {
            return (restriction) => {
                const errors = [];

                if (!this.endTimeTouched) {
                    return errors;
                }

                if (restriction.startTime && restriction.endTime) {
                    const timeDiff = this.calculateTimeDifference(restriction.startTime, restriction.endTime);

                    if (timeDiff !== null) {
                        if (timeDiff <= 0) {
                            errors.push(this.$gettext('End time must be after start time'));
                        } /* else if (timeDiff < 60) {
                            errors.push(this.$gettext('Time window must be at least 1 hour'));
                        }*/
                    }
                }

                return errors;
            };
        },
    },

    mounted() {

        // If we have configurations but none selected, select the first one
        if (this.hasConfigurations && !this.currentSelectedConfiguration) {
            const firstConfig = this.configurations[0];
            if (firstConfig) {
                this.selectConfiguration(firstConfig.key);
            }
        }

    },

    watch: {
        currentSelectedConfiguration(newVal) {
            this.currentSelectedConfigurationIndex = this.configurations.findIndex(config => config.key === newVal);
            this.cancelEditing();
        },
        editingRestrictionId(newVal, oldVal) {
            if (newVal !== oldVal && oldVal) {
                this.isEditingTimeRestrictions = false;
                this.isEditingApps = false;
            }

            if (newVal !== oldVal) {
                this.startTimeTouched = false;
                this.endTimeTouched = false;
            }

            if (newVal && newVal !== oldVal) {
                this.initializeLocalRestriction(newVal);
            } else if (!newVal) {
                this.localRestriction = null;
            }
        }
    },

    methods: {

        handleAppAddedToRestriction(evt) {
            const mdmDeviceId = this.currentConfiguration.value.userInfo.mdmDeviceId;
            const restrictionData = this.restrictions[mdmDeviceId];
            const restrictions = restrictionData?.rules || [];

            if (!restrictions) {
                console.error('No restrictions found for this device');
                return;
            }

            const restriction = restrictions.find(r => r.id === this.editingRestrictionId);

            if (restriction && restriction.apps) {
                const uniqueApps = restriction.apps.filter((app, index, arr) =>
                    arr.findIndex(a => a.packageId === app.packageId) === index
                );

                if (uniqueApps.length !== restriction.apps.length) {
                    restriction.apps = uniqueApps;
                }
            }
        },

        selectConfiguration(configurationName) {
            if (configurationName) {
                this.currentSelectedConfiguration = configurationName;
                // Prevent toggle behavior
                this.$nextTick(() => {
                    this.currentSelectedConfiguration = configurationName;
                });
            }
        },

        checkEachConfiguration(configurationName) {
            let configurationVisibility = true;
            if (this.configurationFilterValue && this.configurationFilterValue.length > 0) {
                configurationVisibility = configurationName &&
                    configurationName.toLowerCase().includes(this.configurationFilterValue.toLowerCase());
            }
            return configurationVisibility;
        },

        currentViewConfigurationCount() {
            let count = 0
            if (this.configurations && Object.keys(this.configurations).length > 0) {
                for (let key in this.configurations) {
                    if (this.checkEachConfiguration(this.configurations[key].key)) {
                        count++
                    }
                }
            }
            return count
        },

        configurationSelectedForMobile() {
            return (
                this.$vuetify.breakpoint.smAndUp ||
                (this.$vuetify.breakpoint.mobile && !this.currentSelectedConfiguration)
            );
        },

        getPhoneTypeDirForDevice,

        handleAppSelection(app) {
        },

        openEditDialog() {
        },

        openAppSelection() {
        },

        handleCreate(timeConditionData) {
        },

        handleUpdate(timeConditionData) {
        },

        editTimeCondition(timeCondition) {
            this.$refs.timeConditionEdit.setEditMode(timeCondition)
        },

        toggleApp(app) {
            const index = this.allowedApps.findIndex(a => a.packageId === app.packageId);
            if (index === -1) {
                this.allowedApps.push(app);
            } else {
                this.allowedApps.splice(index, 1);
            }
        },

        isAppSelected(app) {
            return this.allowedApps.some(a => a.packageId === app.packageId);
        },

        saveChanges() {
            this.showEditDialog = false;
        },

        getAppInitials(appName) {
            return appName
                .split(' ')
                .map(word => word[0])
                .join('')
                .substring(0, 2)
                .toUpperCase();
        },

        setEditingRestrictionId(restrictionId) {
            if (!this.alertIfEditsNotAllowed()) {
                return;
            }
            this.editingRestrictionId = restrictionId;
            this.initializeLocalRestriction(restrictionId);
        },

        initializeLocalRestriction(restrictionId) {
            if (!restrictionId) {
                this.localRestriction = null;
                return;
            }

            // If localRestriction is already set for this ID (e.g., new restriction), don't overwrite it
            if (this.localRestriction && this.localRestriction.id === restrictionId) {
                return;
            }

            const restrictions = this.appUsageRestrictionForCurrentConfiguration;
            if (!restrictions) {
                this.localRestriction = null;
                return;
            }

            const restriction = restrictions.find(r => r.id === restrictionId);
            if (restriction) {
                this.localRestriction = JSON.parse(JSON.stringify(restriction));
            } else {
                this.localRestriction = null;
            }
        },

        toggleAppStore(restrictionId) {
            this.editingRestrictionId = restrictionId;
            this.isEditingApps = !this.isEditingApps;
        },

        handleAppInstalled(app, restriction) {
            if (!restriction.apps) {
                restriction.apps = [];
            }

            const isAlreadyInRestriction = restriction.apps.some(
                a => a.packageId === app.packageId
            );

            if (!isAlreadyInRestriction) {
                const appToAdd = {
                    packageId: app.packageId,
                    appName: app.appName
                };
                restriction.apps.push(appToAdd);
            }
        },

        handleAppInstalledToLocal(app) {
            if (!this.localRestriction) return;

            if (!this.localRestriction.apps) {
                this.localRestriction.apps = [];
            }

            const isAlreadyInRestriction = this.localRestriction.apps.some(
                a => a.packageId === app.packageId
            );

            if (!isAlreadyInRestriction) {
                const appToAdd = {
                    packageId: app.packageId,
                    appName: app.appName
                };
                this.localRestriction.apps.push(appToAdd);
            }
        },

        handleAppAddedToLocalRestriction(evt) {
            if (!this.localRestriction || !this.localRestriction.apps) return;

            const uniqueApps = this.localRestriction.apps.filter((app, index, arr) =>
                arr.findIndex(a => a.packageId === app.packageId) === index
            );

            if (uniqueApps.length !== this.localRestriction.apps.length) {
                this.localRestriction.apps = uniqueApps;
            }
        },

        removeAppFromLocalRestriction(packageId) {
            if (!this.localRestriction || !this.localRestriction.apps) return;

            const appIndex = this.localRestriction.apps.findIndex(app => app.packageId === packageId);
            if (appIndex > -1) {
                const removedApp = this.localRestriction.apps[appIndex];
                this.localRestriction.apps.splice(appIndex, 1);

                this.$refs.snackbar?.showSnackbar(
                    `${removedApp.appName} ${this.$gettext('has been removed from restrictions')}`,
                    'success'
                );

                this.showMenu.value = false;
            }
        },

        updateShowMenu(val) {
            this.showMenu = {
                value: val.value,
                packageName: val.packageName
            };
            this.x = val.x;
            this.y = val.y;
        },

        removeAppFromRestriction(packageId, restriction) {
            if (!restriction || !restriction.apps) return;

            const appIndex = restriction.apps.findIndex(app => app.packageId === packageId);
            if (appIndex > -1) {
                const removedApp = restriction.apps[appIndex];
                restriction.apps.splice(appIndex, 1);

                this.$refs.snackbar?.showSnackbar(
                    `${removedApp.appName} ${this.$gettext('has been removed from restrictions')}`,
                    'success'
                );

                this.showMenu.value = false;
            }
        },

        toggleTimeRestrictionsEdit(restrictionId) {

            let self = this;

            self.editingRestrictionId = restrictionId;

            if (!self.isEditingTimeRestrictions) {
                self.isEditingTimeRestrictions = true;
            } else {
                self.isEditingTimeRestrictions = false;
            }

            // Initialize weekDays array if it doesn't exist
            if (self.isEditingTimeRestrictions) {
                self.ensureWeekDaysArray();
            }

        },

        ensureWeekDaysArray() {
            if (!this.currentConfiguration || !this.currentConfiguration.value || !this.currentConfiguration.value.userInfo || !this.currentConfiguration.value.userInfo.mdmDeviceId) {
                return;
            }

            const mdmDeviceId = this.currentConfiguration.value.userInfo.mdmDeviceId;
            const restrictionData = this.restrictions[mdmDeviceId];
            const restrictions = restrictionData?.rules || [];

            if (restrictions) {
                for (const restriction of restrictions) {
                    if (restriction.weekDays && restriction.weekDays.length === 0) {
                        restriction.weekDays = [1, 2, 3, 4, 5, 6, 7];
                    }
                }
            }
        },

        deleteRestriction(restriction) {

            let self = this;

            if (!self.alertIfEditsNotAllowed()) {
                return;
            }

            // If deleting the restriction currently being edited, cancel editing first
            if (self.editingRestrictionId === restriction.id) {
                self.cancelEditing();
            }

            const deleteRestrictionFn = function (self) {

                const restrictions = self.appUsageRestrictionForCurrentConfiguration;
                const index = restrictions.findIndex(r => r.id === restriction.id);

                if (index !== -1) {
                    restrictions.splice(index, 1);
                    return true;
                } else {
                    console.error('Restriction not found');
                }

                return false;

            };

            const swalOptions = {
                title: self.$gettext('Delete Restriction'),
                description: self.$gettextInterpolate(self.$gettext('Are you sure you want to delete %{ ruleName }?'), { ruleName: restriction.ruleName }),
                successCallback: deleteRestrictionFn,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: self.$gettext('Yes, delete it!'),
                successText: self.$gettext('Restriction has been deleted successfully!')
            };

            self.swalConfirmation(swalOptions);

        },

        isDaySelected(dayIndex, weekDays) {
            // dayIndex now matches weekDaysArray: 0=Sunday, 1=Monday, ..., 6=Saturday
            const dayNumber = dayIndex + 1; // 0+1=1 (Sunday), 1+1=2 (Monday), etc.
            return weekDays.includes(dayNumber);
        },

        handleDragStarted() {
            this.isDragging = true;
        },

        handleDragEnded() {
            this.isDragging = false;
        },

        ifEmptySetNull(value, field, restriction) {
            if (value === "" || value === undefined) {
                restriction[field] = null;
            }
        },

        sanitizeTimeValue(value, field, restriction) {

            // Convert empty string to null
            if (value === "" || value === undefined) {
                restriction[field] = null;
                return;
            }

            // Convert to number if possible
            const num = parseInt(value);
            if (!isNaN(num)) {
                restriction[field] = num;
            } else {
                restriction[field] = null;
            }
        },

        /**
         * Calculate time difference in minutes between two time strings (HH:MM format)
         * @param {string} startTime - Start time in HH:MM format
         * @param {string} endTime - End time in HH:MM format
         * @returns {number} - Time difference in minutes, or null if invalid
         */
        calculateTimeDifference(startTime, endTime) {
            if (!startTime || !endTime) return null;

            const [startHours, startMinutes] = startTime.split(':').map(Number);
            const [endHours, endMinutes] = endTime.split(':').map(Number);

            if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) {
                return null;
            }

            let startTotalMinutes = startHours * 60 + startMinutes;
            let endTotalMinutes = endHours * 60 + endMinutes;

            return endTotalMinutes - startTotalMinutes;
        },

        touchStartTime(restriction) {
            this.startTimeTouched = true;
        },

        touchEndTime(restriction) {
            this.endTimeTouched = true;
        },

        addNewTimeCondition() {

            if (!this.alertIfEditsNotAllowed()) {
                return;
            }

            if (!this.currentConfiguration?.value?.userInfo?.mdmDeviceId) {
                this.$refs.snackbar?.showSnackbar(
                    this.$gettext('No device selected'),
                    'error'
                );
                return;
            }

            const mdmDeviceId = this.currentConfiguration.value.userInfo.mdmDeviceId;

            if (!this.restrictions[mdmDeviceId]) {
                this.restrictions[mdmDeviceId] = { rules: [], appInstallationViaPlayStore: null };
            }

            // Find the highest existing ID to determine the next ID
            const existingIds = this.restrictions[mdmDeviceId].rules.map(r => r.id);
            let nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 101;
            if (nextId < 101) {
                nextId = 101;
            }

            // Create new empty restriction with default values (but don't add to store yet)
            const newRestriction = {
                id: nextId,
                ruleName: this.$gettext('New Time Condition'),
                type: 'quota',
                startTime: '09:00',
                endTime: '20:00',
                allowedTime: null,
                maxByDay: null,
                maxByWeek: null,
                weekDays: [1, 2, 3, 4, 5, 6, 7],
                apps: []
            };

            this.localRestriction = JSON.parse(JSON.stringify(newRestriction));
            this.editingRestrictionId = nextId;
            this.isEditingTimeRestrictions = true;
        },

        saveRestriction() {
            if (!this.localRestriction) return;

            this.startTimeTouched = true;
            this.endTimeTouched = true;

            const startErrors = this.getStartTimeErrors(this.localRestriction);
            const endErrors = this.getEndTimeErrors(this.localRestriction);

            if (startErrors.length > 0 || endErrors.length > 0) {
                this.$refs.snackbar?.showSnackbar(
                    this.$gettext('Please fix validation errors before saving'),
                    'error'
                );
                return;
            }

            if (!this.localRestriction.startTime || !this.localRestriction.endTime) {
                this.$refs.snackbar?.showSnackbar(
                    this.$gettext('Start time and end time are required'),
                    'error'
                );
                return;
            }

            // Validate rule name for non-system configurations
            if (!this.isASystemConfiguration(this.localRestriction.id)) {
                if (!this.localRestriction.ruleName || this.localRestriction.ruleName.trim() === '') {
                    this.$refs.snackbar?.showSnackbar(
                        this.$gettext('Rule name is required'),
                        'error'
                    );
                    return;
                }
            }

            if (this.localRestriction.type === 'quota' && (!this.localRestriction.weekDays || this.localRestriction.weekDays.length === 0)) {
                this.$refs.snackbar?.showSnackbar(
                    this.$gettext('Please select at least one day'),
                    'error'
                );
                return;
            }

            const mdmDeviceId = this.currentConfiguration.value.userInfo.mdmDeviceId;

            if (!this.restrictions[mdmDeviceId]) {
                this.restrictions[mdmDeviceId] = { rules: [], appInstallationViaPlayStore: null };
            }

            const restrictions = this.restrictions[mdmDeviceId].rules;
            const existingIndex = restrictions.findIndex(r => r.id === this.localRestriction.id);

            if (existingIndex !== -1) {
                const updatedRestriction = JSON.parse(JSON.stringify(this.localRestriction));
                restrictions.splice(existingIndex, 1, updatedRestriction);
            } else {
                restrictions.push(JSON.parse(JSON.stringify(this.localRestriction)));
            }

            this.$refs.snackbar?.showSnackbar(
                this.$gettext('Restriction saved successfully'),
                'success'
            );

            this.cancelEditing();
        },

        cancelEditing() {
            this.localRestriction = null;
            this.editingRestrictionId = null;
            this.startTimeTouched = false;
            this.endTimeTouched = false;
            this.isEditingTimeRestrictions = false;
            this.isEditingApps = false;
        },

        secondsToHHMM(seconds) {
            if (seconds == null || isNaN(seconds)) return '';
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        },
        updateAllowedTimeFromHHMM(hhmm, restriction) {
            if (!hhmm) {
                restriction.allowedTime = null;
                return;
            }
            // Mask ensures format is always HH:MM or empty
            const [h, m] = hhmm.split(':').map(Number);
            if (!isNaN(h) && !isNaN(m)) {
                restriction.allowedTime = (h * 3600) + (m * 60);
            } else {
                restriction.allowedTime = null;
            }
        },
        toggleAllDays(restriction) {
            if (restriction.weekDays && restriction.weekDays.length === 7) {
                restriction.weekDays = [];
            } else {
                restriction.weekDays = [1, 2, 3, 4, 5, 6, 7];
            }
        },
        getAppIcon(app) {
            return app.iconUrl;
        },
        getRuleIcon(rule) {
            if (this.isGlobalUsageRule(rule)) return 'mdi-clock-outline';
            return 'mdi-chart-pie';
        },
        isGlobalUsageRule(rule) {
            return rule.ruleName === 'Global Usage Time' || rule.ruleName === 'Night Break';
        },
        formatWeekdays(weekDays) {
            let self = this;
            if (!weekDays || weekDays.length === 0) return '';
            return weekDays.map(d => self.weekDaysArray[d - 1]).join('');
        },
        /**
         * Return just the hour from the time string, for now
         * @param time
         */
        getInt(time) {
            if (!time) return null;
            return parseInt(time.split(':')[0]);
        },
        getCurrentRestrictionInEditingState() {

            let self = this;

            if (!self.editingRestrictionId) {
                return [];
            }

            let r = this.appUsageRestrictionForCurrentConfiguration.find(r => r.id === self.editingRestrictionId);

            if (!r) {
                return []
            } else {
                return [r];
            }

        },

        isGlobalUsageRule(rule) {
            return rule.ruleName === 'Global Usage Time' || rule.ruleName === 'Night Break';
        },

        isASystemConfiguration(ruleId) {
            return ruleId <= 100;
        },

        getSystemRuleName(ruleId) {

            if (ruleId === 1) return this.$gettext('Global Usage Time');
            if (ruleId === 2) return this.$gettext('Social Networks');
            if (ruleId === 3) return this.$gettext('Streaming');
            if (ruleId === 4) return this.$gettext('Games');

            return '';

        },

        getSystemOrNormalRuleName(restriction) {

            let self = this;

            if (self.isASystemConfiguration(restriction.id)) {
                const ruleName = self.getSystemRuleName(restriction.id);
                if (ruleName.length) {
                    return ruleName;
                } else {
                    console.error('Unknown ruleId', restriction.id);
                    return restriction.ruleName;
                }
            } else {
                return restriction.ruleName;
            }

        }

    },

    created() {
        // Ensure weekDays is initialized when component is created
        if (this.restriction && !this.restriction.weekDays) {
            this.$set(this.restriction, 'weekDays', []);
        }
    },

    beforeDestroy() {
        this.$root.$off('drag-started');
        this.$root.$off('drag-ended');
    }
}
</script>

<style lang="scss" scoped>
.border-right-divider {
    border-right: 1px solid #e0e0e0;
}

.app-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    row-gap: 20px;
    column-gap: 10px;
}

.app-grid-right {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
}

/* Custom, iPhone Retina */
@media only screen and (min-width: 320px) {
    .border-right-divider {
        border-right: 1px solid #e0e0e0;
    }
}

.time-input {
    font-size: 16px;

    ::v-deep .v-input__slot {
        min-height: 32px !important;
    }

    input {
        text-align: center;
    }
}

.gap-4 {
    gap: 1rem;
}

.time-field {
    max-width: 100px;

    ::v-deep .v-input__slot {
        min-height: 36px;
    }
}

.app-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.app-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
}

.app-initials {
    font-size: 16px;
    font-weight: 500;
    color: #666;
}

/*
.app-add-button {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    border: 2px dashed rgba(0, 0, 0, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: var(--v-primary-base);
        background-color: rgba(0, 0, 0, 0.04);
    }
}
 */

.weekdays-preview {
    font-family: monospace; // For consistent letter spacing
    letter-spacing: 1px;

    span {
        font-size: 14px;
        font-weight: 500;
    }

}

/** Droppable */

.draggable-container {
    transition: all 0.2s ease !important;
    min-height: 100px !important;
    width: 100% !important;
    border: 2px dashed transparent !important;
    border-radius: 4px !important;
    padding: 8px !important;

    /* When dragging is happening anywhere (element being dragged over) */
    &.sortable-ghost {
        background-color: rgba(25, 118, 210, 0.05) !important;
        border: 2px dashed #1976D2 !important;
    }

    /* The element being dragged */
    &.sortable-drag {
        opacity: 0.5 !important;
    }

    /* The element that was chosen to drag */
    &.sortable-chosen {
        background-color: rgba(25, 118, 210, 0.05) !important;
    }
}

.droppable-highlight {
    border: 2px dashed #1976d2; // Highlight style for droppable area
    background-color: rgba(25, 118, 210, 0.1);
}

.drag-fallback {
    width: 80px !important;
    height: 80px !important;
    overflow: hidden !important;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2) !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    background-color: white !important;
    border-radius: 8px !important;
    opacity: 0.9 !important;
    z-index: 1000 !important;
    pointer-events: none !important;
}

.app-icons-section {
    min-height: 64px;
}

.app-icon-container {
    width: 72px;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;

    ::v-deep .v-avatar {
        width: 48px !important;
        height: 48px !important;
        min-width: 48px !important;
        min-height: 48px !important;
    }
}

.app-icon-container-concise {
    width: 54px;
}
</style>
